import {
  recognitionService,
  FetchRecognitionRequest,
  RecognitionPage,
  Recognition,
  UpdateRecognitionRequest,
  RejectRecognitionRequest,
  AddReactionRequest,
} from '@aslanpay/portal-common-lib';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const useListPendingApprovals = (params?: FetchRecognitionRequest, enabled = true) => {
  return useQuery<RecognitionPage, Error>({
    queryKey: ['listPendingApprovals', params],
    queryFn: () => recognitionService.listPendingApprovals(params),
    enabled,
  });
};

const useGetRecognition = (recognitionId: string | undefined, enabled = true) => {
  return useQuery<Recognition, Error>({
    queryKey: ['getRecognition', recognitionId],
    queryFn: () => {
      if (!recognitionId) throw new Error('recognitionId is required');
      return recognitionService.getRecognition(recognitionId);
    },
    enabled: enabled && !!recognitionId,
  });
};

const useUpdateRecognition = (recognitionId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: UpdateRecognitionRequest) => {
      if (!recognitionId) throw new Error('recognitionId is required');
      return recognitionService.updateRecognition(recognitionId, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getRecognition', recognitionId] });
      queryClient.invalidateQueries({ queryKey: ['listPendingApprovals'] });
    },
  });
};

const useDeleteRecognition = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (recognitionId: string) => recognitionService.deleteRecognition(recognitionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listPendingApprovals'] });
    },
  });
};

const useApproveRecognition = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (recognitionId: string) => recognitionService.approveRecognition(recognitionId),
    onSuccess: (_data, recognitionId) => {
      queryClient.invalidateQueries({ queryKey: ['getRecognition', recognitionId] });
      queryClient.invalidateQueries({ queryKey: ['listPendingApprovals'] });
    },
  });
};

const useRejectRecognition = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      recognitionId,
      body,
    }: {
      recognitionId: string;
      body: RejectRecognitionRequest;
    }) => recognitionService.rejectRecognition(recognitionId, body),
    onSuccess: (_data, { recognitionId }) => {
      queryClient.invalidateQueries({ queryKey: ['getRecognition', recognitionId] });
      queryClient.invalidateQueries({ queryKey: ['listPendingApprovals'] });
    },
  });
};

type UseInfiniteListRecognitionsParams = Omit<FetchRecognitionRequest, 'page' | 'size'>;

const PAGE_SIZE = 6;

const useInfiniteListRecognitions = (
  params: UseInfiniteListRecognitionsParams = {},
  enabled = true
) => {
  const { companyId, groupIds, valueIds, query, createdFrom, createdTo } = params;

  return useInfiniteQuery<RecognitionPage, Error>({
    queryKey: ['listRecognitions', companyId, groupIds, valueIds, query, createdFrom, createdTo],
    queryFn: ({ pageParam = 0 }) =>
      recognitionService.fetchRecognitions({
        statuses: ['APPROVED'],
        companyId,
        groupIds,
        valueIds,
        query,
        createdFrom,
        createdTo,
        page: pageParam as number,
        size: PAGE_SIZE,
      }),
    getNextPageParam: (lastPage) => {
      const hasMore = (lastPage.pageNumber + 1) * lastPage.pageSize < lastPage.total;
      return hasMore ? lastPage.pageNumber + 1 : undefined;
    },
    enabled,
  });
};

const useHideRecognition = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (recognitionId: string) => recognitionService.hideRecognition(recognitionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listRecognitions'] });
    },
  });
};

const useUnhideRecognition = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (recognitionId: string) => recognitionService.unhideRecognition(recognitionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listRecognitions'] });
    },
  });
};

const useAddReaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ recognitionId, body }: { recognitionId: string; body: AddReactionRequest }) =>
      recognitionService.addReaction(recognitionId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listRecognitions'] });
    },
  });
};

const useDeleteReaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      recognitionId,
      reactionType,
    }: {
      recognitionId: string;
      reactionType: string;
    }) => recognitionService.deleteReaction(recognitionId, reactionType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listRecognitions'] });
    },
  });
};

const useRecognition = {
  useListPendingApprovals,
  useGetRecognition,
  useUpdateRecognition,
  useDeleteRecognition,
  useApproveRecognition,
  useRejectRecognition,
  useInfiniteListRecognitions,
  useHideRecognition,
  useUnhideRecognition,
  useAddReaction,
  useDeleteReaction,
};

export default useRecognition;
